/**
 * Admin Users & Access Page
 * Based on Figma design - manages users, roles, and permissions
 */

import { useState, useEffect } from 'react';
import { Users, Shield, Search, UserPlus, Mail, Edit2, Trash2, Loader2, Check, Eye } from 'lucide-react';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useToast } from '@/hooks/use-toast';
import { useAuthToken, useAuth } from '@/hooks/use-auth';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface User {
  id: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  role?: { id: string; name: string } | null;
  status?: 'Active' | 'Invited' | 'Suspended';
}

interface Permission {
  id: string;
  name: string;
  description: string | null;
}

interface Role {
  id: string;
  name: string;
  description: string | null;
  scope?: 'Organization' | 'Workspace' | 'Project';
  permissions: Array<{
    permission: Permission;
  }>;
  _count?: {
    members: number;
  };
}

export default function AdminUsersPage() {
  const { hasPermission } = useAuth();
  const canManageUsers = hasPermission('user:manage');
  
  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  
  // Edit role state
  const [editRoleModal, setEditRoleModal] = useState<{ open: boolean; role: Role | null }>({ open: false, role: null });
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleDescription, setEditRoleDescription] = useState('');
  const [isSavingRole, setIsSavingRole] = useState(false);
  
  // Create role state
  const [createRoleModal, setCreateRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState<string[]>([]);
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  
  // Edit user state
  // TODO: TEMPORARY - password field is for development/testing only. Remove when proper SSO is implemented.
  const [editUserModal, setEditUserModal] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [editUserName, setEditUserName] = useState('');
  const [editUserRole, setEditUserRole] = useState('');
  const [editUserPassword, setEditUserPassword] = useState('');
  const [isSavingUser, setIsSavingUser] = useState(false);
  
  const { toast } = useToast();
  const token = useAuthToken();

  // Fetch functions - extracted so they can be called on demand
  const fetchUsers = async () => {
    if (!token) return;
    setIsLoadingUsers(true);
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchRoles = async () => {
    if (!token) return;
    setIsLoadingRoles(true);
    try {
      const response = await fetch('/api/roles', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch roles');
      const data = await response.json();
      setRoles(data.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load roles',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingRoles(false);
    }
  };

  // Handle tab switching with data refresh
  const handleTabChange = (tab: 'users' | 'roles') => {
    setActiveTab(tab);
    if (tab === 'users') {
      fetchUsers();
    } else {
      fetchRoles();
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchUsers();
  }, [token]);

  useEffect(() => {
    fetchRoles();
  }, [token]);

  // Fetch all permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/permissions', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch permissions');
        const data = await response.json();
        setAllPermissions(data.data || []);
      } catch (error) {
        console.error('Failed to load permissions:', error);
      }
    };
    fetchPermissions();
  }, [token]);

  // Open edit role modal
  const openEditRoleModal = (role: Role) => {
    setEditRoleName(role.name);
    setEditRoleDescription(role.description || '');
    setSelectedPermissions(role.permissions.map(p => p.permission.id));
    setEditRoleModal({ open: true, role });
  };

  // Toggle permission selection
  const togglePermission = (permissionId: string) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Save role changes
  const handleSaveRole = async () => {
    if (!editRoleModal.role || !token) return;

    setIsSavingRole(true);
    try {
      const response = await fetch(`/api/roles/${editRoleModal.role.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editRoleName,
          description: editRoleDescription,
          permissionIds: selectedPermissions,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to update role');
      }

      const data = await response.json();
      
      // Update role in local state
      setRoles(prev => prev.map(r => r.id === editRoleModal.role!.id ? data.data : r));
      
      toast({
        title: 'Role updated',
        description: `"${editRoleName}" has been updated successfully.`,
      });
      
      setEditRoleModal({ open: false, role: null });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update role',
        variant: 'destructive',
      });
    } finally {
      setIsSavingRole(false);
    }
  };

  // Create new role
  const handleCreateRole = async () => {
    if (!newRoleName || !token) return;

    setIsCreatingRole(true);
    try {
      const response = await fetch('/api/roles', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newRoleName.toLowerCase().replace(/\s+/g, '_'),
          description: newRoleDescription,
          permissionIds: newRolePermissions,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to create role');
      }

      const data = await response.json();
      
      // Add new role to list
      setRoles(prev => [...prev, data.data]);
      
      toast({
        title: 'Role created',
        description: `"${newRoleName}" has been created successfully.`,
      });
      
      // Reset form and close modal
      setCreateRoleModal(false);
      setNewRoleName('');
      setNewRoleDescription('');
      setNewRolePermissions([]);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create role',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingRole(false);
    }
  };

  // Toggle permission for new role
  const toggleNewRolePermission = (permissionId: string) => {
    setNewRolePermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Group permissions by category (e.g., project:*, artifact:*, etc.)
  const groupedPermissions = allPermissions.reduce((acc, permission) => {
    const category = permission.name.split(':')[0];
    if (!acc[category]) acc[category] = [];
    acc[category].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  const filteredUsers = users.filter(user =>
    (user.name?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (user: User) => {
    // Determine status based on lastLoginAt
    const status = user.lastLoginAt ? 'Active' : 'Invited';
    const styles = {
      'Active': 'bg-success-bg text-success-text border-success-bg',
      'Invited': 'bg-info-bg text-info-text border-info-bg',
      'Suspended': 'bg-error-bg text-error-text border-error-bg'
    };
    return (
      <span className={`px-2.5 py-1 border rounded-md text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const getRoleBadge = (roleName: string) => {
    return (
      <span className="px-2.5 py-1 bg-brand-100 text-brand-800 border border-brand-200 rounded-md text-xs font-medium">
        {roleName}
      </span>
    );
  };

  const getScopeBadge = (scope: string) => {
    const styles: Record<string, string> = {
      'Organization': 'bg-brand-100 text-brand-800 border-brand-200',
      'Workspace': 'bg-info-bg text-info-text border-info-bg',
      'Project': 'bg-success-bg text-success-text border-success-bg'
    };
    return (
      <span className={`px-2.5 py-1 border rounded-md text-xs font-medium ${styles[scope] || styles['Project']}`}>
        {scope}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-CA'); // YYYY-MM-DD format
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteUser = async () => {
    if (!deleteModal.user || !token) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/users/${deleteModal.user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to delete user');
      }

      // Remove user from local state
      setUsers(prev => prev.filter(u => u.id !== deleteModal.user!.id));
      
      toast({
        title: 'User removed',
        description: `${deleteModal.user.name || deleteModal.user.email} has been removed.`,
      });
      setDeleteModal({ open: false, user: null });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete user',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendInvite = (user: User) => {
    toast({
      title: 'Invitation sent',
      description: `Invitation resent to ${user.email}`,
    });
  };

  // Open edit user modal
  const openEditUserModal = (user: User) => {
    setEditUserName(user.name || '');
    setEditUserRole(user.role?.id || '');
    setEditUserPassword(''); // Always start with empty password field
    setEditUserModal({ open: true, user });
  };

  // Save user changes
  const handleSaveUser = async () => {
    if (!editUserModal.user || !token) return;

    setIsSavingUser(true);
    try {
      // Only include password in payload if it was changed
      const payload: { name: string; roleId: string | null; password?: string } = {
        name: editUserName,
        roleId: editUserRole || null,
      };
      if (editUserPassword) {
        payload.password = editUserPassword;
      }

      const response = await fetch(`/api/users/${editUserModal.user.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to update user');
      }

      const data = await response.json();
      
      // Update user in local state
      setUsers(prev => prev.map(u => u.id === editUserModal.user!.id ? data.data : u));
      
      toast({
        title: 'User updated',
        description: `"${editUserName}" has been updated successfully.`,
      });
      
      setEditUserModal({ open: false, user: null });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update user',
        variant: 'destructive',
      });
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        variant: 'destructive',
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    if (!token) return;

    setIsInviting(true);
    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: inviteName || null,
          email: inviteEmail,
          roleId: inviteRole || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to invite user');
      }

      const data = await response.json();
      
      // Add new user to the list
      setUsers(prev => [data.data, ...prev]);
      
      toast({
        title: 'User invited',
        description: `Invitation sent to ${inviteEmail}`,
      });
      
      // Reset form and close modal
      setInviteModal(false);
      setInviteName('');
      setInviteEmail('');
      setInviteRole('');
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to invite user',
        variant: 'destructive',
      });
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header - white background extends to right edge */}
      <div className="shrink-0 bg-white border-b border-surface-border">
        <div className="px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium text-gray-900 tracking-tight">
              User & Access
            </h1>
            <p className="text-base text-gray-500 mt-1">
              Manage users, roles, and permissions
            </p>
          </div>
          {canManageUsers && (
            activeTab === 'users' ? (
              <button
                onClick={() => setInviteModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-brand-700 text-white rounded-lg hover:bg-brand-800 transition-colors font-medium"
              >
                <UserPlus className="w-5 h-5" />
                Invite User
              </button>
            ) : (
              <button
                onClick={() => setCreateRoleModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-brand-700 text-white rounded-lg hover:bg-brand-800 transition-colors font-medium"
              >
                <Shield className="w-5 h-5" />
                Add New Role
              </button>
            )
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => handleTabChange('users')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
              activeTab === 'users'
                ? 'bg-brand-100 text-brand-800'
                : 'text-gray-600 hover:bg-surface-secondary'
            }`}
          >
            <Users className="w-5 h-5" />
            Users ({users.length})
          </button>
          {canManageUsers && (
            <button
              onClick={() => handleTabChange('roles')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                activeTab === 'roles'
                  ? 'bg-brand-100 text-brand-800'
                  : 'text-gray-600 hover:bg-surface-secondary'
              }`}
            >
              <Shield className="w-5 h-5" />
              Roles ({roles.length})
            </button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={activeTab === 'users' ? 'Search users...' : 'Search roles...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-surface-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
        </div>
      </div>

      {/* Content - gray background extends to right edge */}
      <div className="flex-1 overflow-auto bg-surface-secondary">
        <div className="container px-8 py-6">
        {activeTab === 'users' ? (
          /* Users Table */
          <div className="bg-white border border-surface-border rounded-xl overflow-hidden">
            {isLoadingUsers ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-0">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/4" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                ))}
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No users found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">User</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Role</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Last Active</th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <UserAvatar 
                            name={user.name} 
                            email={user.email} 
                            avatarUrl={user.avatarUrl}
                            size="md"
                          />
                          <div>
                            <div className="text-gray-900 font-medium">{user.name || 'Unknown'}</div>
                            <div className="text-gray-500 text-sm">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {user.role ? getRoleBadge(user.role.name) : (
                          <span className="text-gray-400 text-sm italic">No role assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(user)}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {formatDate(user.lastLoginAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {canManageUsers ? (
                            <>
                              <button
                                onClick={() => openEditUserModal(user)}
                                className="p-1.5 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                                title="Edit user"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              {!user.lastLoginAt && (
                                <button
                                  onClick={() => handleResendInvite(user)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Resend invitation"
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => setDeleteModal({ open: true, user })}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Remove user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <span className="p-1.5 text-gray-400" title="View only">
                              <Eye className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          /* Roles Grid */
          <div className="space-y-4">
            {isLoadingRoles ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-surface-border rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <Skeleton className="w-10 h-10 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-1/4" />
                      <Skeleton className="h-4 w-1/3" />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-6 w-28" />
                  </div>
                </div>
              ))
            ) : filteredRoles.length === 0 ? (
              <div className="bg-white border border-surface-border rounded-lg p-12 text-center">
                <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No roles found</p>
              </div>
            ) : (
              filteredRoles.map((role) => (
                <div
                  key={role.id}
                  className="bg-white border border-surface-border rounded-lg p-6 hover:shadow-card-hover transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-brand-700" />
                      </div>
                      <div>
                        <h3 className="text-gray-900 font-medium">{role.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {getScopeBadge(role.scope || 'Project')}
                          <span className="text-gray-500 text-sm">
                            {role._count?.members || 0} users
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => openEditRoleModal(role)}
                      className="p-2 text-gray-600 hover:bg-surface-secondary rounded-lg transition-colors"
                      title="Edit role permissions"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>

                  {role.description && (
                    <p className="text-gray-600 text-sm mb-4">{role.description}</p>
                  )}

                  <div>
                    <h4 className="text-gray-700 text-sm font-medium mb-2">Permissions:</h4>
                    <div className="flex flex-wrap gap-2">
                      {role.permissions.length > 0 ? (
                        role.permissions.map((p) => (
                          <span
                            key={p.permission.id}
                            className="px-2.5 py-1 bg-gray-50 text-gray-700 border border-gray-200 rounded text-xs"
                          >
                            {p.permission.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No permissions assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        </div>
      </div>

      {/* Edit User Modal */}
      <Dialog open={editUserModal.open} onOpenChange={(open) => !open && setEditUserModal({ open: false, user: null })}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-full">
                <Edit2 className="w-5 h-5 text-indigo-600" />
              </div>
              Edit User
            </DialogTitle>
            <DialogDescription>
              Update user details and role assignment
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address
              </label>
              <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
                {editUserModal.user?.email}
              </div>
            </div>

            {/* Name field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                value={editUserName}
                onChange={(e) => setEditUserName(e.target.value)}
                placeholder="Enter user's name"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Password field - TEMPORARY for dev/testing */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Password <span className="text-gray-400 font-normal">(temporary)</span>
              </label>
              <input
                type="text"
                value={editUserPassword}
                onChange={(e) => setEditUserPassword(e.target.value)}
                placeholder="Set new password (leave empty to keep current)"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-amber-600">
                ⚠️ Temporary dev feature - will be replaced by SSO
              </p>
            </div>

            {/* Role field with custom styling */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Role
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {/* No role option */}
                <label
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    editUserRole === ''
                      ? 'bg-gray-50 border-gray-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors ${
                      editUserRole === ''
                        ? 'bg-gray-500 border-gray-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {editUserRole === '' && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="userRole"
                    value=""
                    checked={editUserRole === ''}
                    onChange={() => setEditUserRole('')}
                    className="sr-only"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700">
                      No role assigned
                    </div>
                  </div>
                </label>
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      editUserRole === role.id
                        ? 'bg-indigo-50 border-indigo-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors ${
                        editUserRole === role.id
                          ? 'bg-indigo-600 border-indigo-600'
                          : 'border-gray-300'
                      }`}
                    >
                      {editUserRole === role.id && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <input
                      type="radio"
                      name="userRole"
                      value={role.id}
                      checked={editUserRole === role.id}
                      onChange={() => setEditUserRole(role.id)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 capitalize">
                        {role.name}
                      </div>
                      {role.description && (
                        <div className="text-xs text-gray-500">
                          {role.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setEditUserModal({ open: false, user: null })}
              disabled={isSavingUser}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveUser}
              disabled={isSavingUser}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isSavingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, user: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              Remove User
            </DialogTitle>
            <DialogDescription className="pt-3">
              Are you sure you want to remove <span className="font-medium text-gray-900">"{deleteModal.user?.name || deleteModal.user?.email}"</span>?
              <br /><br />
              <span className="text-gray-500">They will lose access to all projects and workspaces.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteModal({ open: false, user: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Modal */}
      <Dialog open={inviteModal} onOpenChange={(open) => {
        if (!open) {
          setInviteName('');
          setInviteEmail('');
          setInviteRole('');
        }
        setInviteModal(open);
      }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-full">
                <UserPlus className="w-5 h-5 text-indigo-600" />
              </div>
              Invite User
            </DialogTitle>
            <DialogDescription>
              Send an invitation to join your workspace. They will receive an email with instructions to set up their account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Full Name <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="John Smith"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                The user can update their name after signing in
              </p>
            </div>

            {/* Email field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Role field with styled cards */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Role <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {/* No role option */}
                <label
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    inviteRole === ''
                      ? 'bg-gray-50 border-gray-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors ${
                      inviteRole === ''
                        ? 'bg-gray-500 border-gray-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {inviteRole === '' && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="inviteUserRole"
                    value=""
                    checked={inviteRole === ''}
                    onChange={() => setInviteRole('')}
                    className="sr-only"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700">
                      No role assigned
                    </div>
                    <div className="text-xs text-gray-500">
                      Role can be assigned later
                    </div>
                  </div>
                </label>
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      inviteRole === role.id
                        ? 'bg-indigo-50 border-indigo-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors ${
                        inviteRole === role.id
                          ? 'bg-indigo-600 border-indigo-600'
                          : 'border-gray-300'
                      }`}
                    >
                      {inviteRole === role.id && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <input
                      type="radio"
                      name="inviteUserRole"
                      value={role.id}
                      checked={inviteRole === role.id}
                      onChange={() => setInviteRole(role.id)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 capitalize">
                        {role.name}
                      </div>
                      {role.description && (
                        <div className="text-xs text-gray-500">
                          {role.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setInviteModal(false);
                setInviteName('');
                setInviteEmail('');
                setInviteRole('');
              }}
              disabled={isInviting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteUser}
              disabled={isInviting || !inviteEmail}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isInviting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Invitation'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Modal */}
      <Dialog open={editRoleModal.open} onOpenChange={(open) => !open && setEditRoleModal({ open: false, role: null })}>
        <DialogContent className="sm:max-w-[600px] h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-full">
                <Shield className="w-5 h-5 text-indigo-600" />
              </div>
              Edit Role
            </DialogTitle>
            <DialogDescription>
              Update role details and permissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 py-4 space-y-6">
            {/* Role name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Role Name
              </label>
              <input
                type="text"
                value={editRoleName}
                onChange={(e) => setEditRoleName(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              />
            </div>

            {/* Role description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description
              </label>
              <input
                type="text"
                value={editRoleDescription}
                onChange={(e) => setEditRoleDescription(e.target.value)}
                placeholder="Brief description of this role..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              />
            </div>

            {/* Permissions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Permissions
              </label>
              <div className="space-y-3">
                {Object.entries(groupedPermissions).map(([category, permissions]) => (
                  <div key={category} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900 capitalize">
                        {category}
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          const categoryPermIds = permissions.map(p => p.id);
                          const allSelected = categoryPermIds.every(id => selectedPermissions.includes(id));
                          if (allSelected) {
                            setSelectedPermissions(prev => prev.filter(id => !categoryPermIds.includes(id)));
                          } else {
                            setSelectedPermissions(prev => [...new Set([...prev, ...categoryPermIds])]);
                          }
                        }}
                        className="text-xs text-indigo-600 hover:text-indigo-700"
                      >
                        {permissions.every(p => selectedPermissions.includes(p.id)) ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {permissions.map((permission) => (
                        <label
                          key={permission.id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            selectedPermissions.includes(permission.id)
                              ? 'bg-indigo-50 border-indigo-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                              selectedPermissions.includes(permission.id)
                                ? 'bg-indigo-600 border-indigo-600'
                                : 'border-gray-300'
                            }`}
                          >
                            {selectedPermissions.includes(permission.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => togglePermission(permission.id)}
                            className="sr-only"
                          />
                          <div>
                            <div className="text-sm text-gray-900">
                              {permission.name.split(':')[1]}
                            </div>
                            {permission.description && (
                              <div className="text-xs text-gray-500">
                                {permission.description}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0 border-t pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => setEditRoleModal({ open: false, role: null })}
              disabled={isSavingRole}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRole}
              disabled={isSavingRole || !editRoleName}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isSavingRole ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Role Modal */}
      <Dialog open={createRoleModal} onOpenChange={(open) => {
        if (!open) {
          setNewRoleName('');
          setNewRoleDescription('');
          setNewRolePermissions([]);
        }
        setCreateRoleModal(open);
      }}>
        <DialogContent className="sm:max-w-[600px] h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-full">
                <Shield className="w-5 h-5 text-indigo-600" />
              </div>
              Add New Role
            </DialogTitle>
            <DialogDescription>
              Create a new role with specific permissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 py-4 space-y-6">
            {/* Role name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g., Developer, QA Tester, Stakeholder"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              />
              <p className="mt-1.5 text-xs text-gray-500">
                Will be converted to lowercase with underscores (e.g., "QA Tester" → "qa_tester")
              </p>
            </div>

            {/* Role description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description
              </label>
              <input
                type="text"
                value={newRoleDescription}
                onChange={(e) => setNewRoleDescription(e.target.value)}
                placeholder="Brief description of this role's purpose..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              />
            </div>

            {/* Permissions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Permissions
              </label>
              <div className="space-y-3">
                {Object.entries(groupedPermissions).map(([category, permissions]) => (
                  <div key={category} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900 capitalize">
                        {category}
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          const categoryPermIds = permissions.map(p => p.id);
                          const allSelected = categoryPermIds.every(id => newRolePermissions.includes(id));
                          if (allSelected) {
                            setNewRolePermissions(prev => prev.filter(id => !categoryPermIds.includes(id)));
                          } else {
                            setNewRolePermissions(prev => [...new Set([...prev, ...categoryPermIds])]);
                          }
                        }}
                        className="text-xs text-indigo-600 hover:text-indigo-700"
                      >
                        {permissions.every(p => newRolePermissions.includes(p.id)) ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {permissions.map((permission) => (
                        <label
                          key={permission.id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            newRolePermissions.includes(permission.id)
                              ? 'bg-indigo-50 border-indigo-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                              newRolePermissions.includes(permission.id)
                                ? 'bg-indigo-600 border-indigo-600'
                                : 'border-gray-300'
                            }`}
                          >
                            {newRolePermissions.includes(permission.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={newRolePermissions.includes(permission.id)}
                            onChange={() => toggleNewRolePermission(permission.id)}
                            className="sr-only"
                          />
                          <div>
                            <div className="text-sm text-gray-900">
                              {permission.name.split(':')[1]}
                            </div>
                            {permission.description && (
                              <div className="text-xs text-gray-500">
                                {permission.description}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0 border-t pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setCreateRoleModal(false);
                setNewRoleName('');
                setNewRoleDescription('');
                setNewRolePermissions([]);
              }}
              disabled={isCreatingRole}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateRole}
              disabled={isCreatingRole || !newRoleName}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isCreatingRole ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
